import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import type { MasterDataEntry, MasterDataKind } from './api';
import {
  COLUMN_SPECS,
  defaultValuesFor,
  FIELD_SPECS,
  MASTER_DATA_KINDS,
  toInputValue,
} from './fields';
import { MasterDataForm } from './MasterDataForm';
import {
  useCreateMasterData,
  useDeleteMasterData,
  useMasterData,
  useUpdateMasterData,
} from './queries';

/**
 * Die Stammdatenverwaltung.
 *
 * Eine Seite fuer sieben Arten: links die Auswahl, rechts die Tabelle. Sieben
 * getrennte Seiten waeren siebenmal dasselbe Geruest -- und der Administrator
 * muesste zwischen ihnen navigieren, obwohl er beim Einrichten eines
 * Mandanten ohnehin alle nacheinander durchgeht.
 *
 * Die Kategorien und die Prioritaetsmatrix haben eigene Seiten: Ein Baum und
 * ein Raster passen nicht in dieselbe Tabelle.
 */
export function MasterDataPage() {
  const { t } = useTranslation();
  const [kind, setKind] = useState<MasterDataKind>('queue');

  return (
    <>
      <PageHeader title={t('masterdata.title')} subtitle={t('masterdata.subtitle')} />

      <div className="toolbar" role="group" aria-label={t('masterdata.title')}>
        {MASTER_DATA_KINDS.map((entry) => (
          <Button
            key={entry}
            variant={kind === entry ? 'secondary' : 'ghost'}
            aria-pressed={kind === entry}
            onClick={() => {
              setKind(entry);
            }}
          >
            {t(`masterdata.${entry}`)}
          </Button>
        ))}
      </div>

      {/* Der Schluessel setzt die Tabelle beim Wechsel zurueck -- sonst
          bliebe ein geoeffneter Bearbeitungsdialog der vorigen Art stehen. */}
      <MasterDataTable key={kind} kind={kind} />
    </>
  );
}

function MasterDataTable({ kind }: { kind: MasterDataKind }) {
  const { t } = useTranslation();
  const entries = useMasterData(kind);
  const create = useCreateMasterData(kind);
  const update = useUpdateMasterData(kind);
  const remove = useDeleteMasterData(kind);

  const [editing, setEditing] = useState<MasterDataEntry | null>(null);
  const [creating, setCreating] = useState(false);

  const specs = FIELD_SPECS[kind];
  const columns = COLUMN_SPECS[kind];

  return (
    <>
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
        <Button
          variant="primary"
          onClick={() => {
            setCreating(true);
          }}
        >
          {t('masterdata.add')}
        </Button>
      </div>

      {entries.isError ? <ErrorNotice error={entries.error} /> : null}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      <div className="card card--flush">
        {entries.isPending ? <SkeletonTable columns={2} /> : null}

        {entries.data?.length === 0 ? (
          <EmptyState
            icon="masterData"
            title={t('masterdata.empty')}
            hint={t('masterdata.emptyHint')}
          />
        ) : null}

        {entries.data && entries.data.length > 0 ? (
          <div className="table-wrapper table-wrapper--tall">
            <table className="table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column} scope="col">
                      {t(labelKeyFor(column))}
                    </th>
                  ))}
                  <th scope="col">
                    <span className="visually-hidden">{t('masterdata.edit')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.data.map((entry) => (
                  <tr key={entry.id}>
                    {columns.map((column) => (
                      <td key={column}>
                        <CellValue column={column} entry={entry} />
                      </td>
                    ))}
                    <td>
                      <div className="row" style={{ justifyContent: 'flex-end' }}>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setEditing(entry);
                          }}
                        >
                          {t('masterdata.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          busy={remove.isPending && remove.variables === entry.id}
                          onClick={() => {
                            // Bewusst eine Rueckfrage: Loeschen ist zwar weich,
                            // aber der Eintrag verschwindet aus allen
                            // Auswahllisten -- das faellt sonst erst dem
                            // naechsten Bearbeiter auf.
                            if (window.confirm(t('masterdata.deleteConfirm'))) {
                              remove.mutate(entry.id);
                            }
                          }}
                        >
                          {t('masterdata.delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <FormDialog
        open={creating}
        title={t('masterdata.add')}
        onOpenChange={setCreating}
        specs={specs}
        initialValues={defaultValuesFor(specs)}
        isEdit={false}
        busy={create.isPending}
        error={create.isError ? create.error : null}
        submitLabel={t('masterdata.add')}
        onSubmit={(values) => {
          create.mutate(values, {
            onSuccess: () => {
              setCreating(false);
            },
          });
        }}
      />

      <FormDialog
        open={editing !== null}
        title={t('masterdata.edit')}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        specs={specs}
        initialValues={editing ?? {}}
        isEdit
        busy={update.isPending}
        error={update.isError ? update.error : null}
        submitLabel={t('app.save')}
        onSubmit={(values) => {
          if (!editing) return;
          update.mutate(
            { id: editing.id, payload: values },
            {
              onSuccess: () => {
                setEditing(null);
              },
            },
          );
        }}
      />
    </>
  );
}

interface FormDialogProps {
  open: boolean;
  title: string;
  onOpenChange: (open: boolean) => void;
  specs: ReturnType<typeof FIELD_SPECS.queue.slice>;
  initialValues: Record<string, unknown>;
  isEdit: boolean;
  busy: boolean;
  error: unknown;
  submitLabel: string;
  onSubmit: (values: Record<string, unknown>) => void;
}

function FormDialog({ open, title, onOpenChange, ...rest }: FormDialogProps) {
  if (!open) return null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog__overlay" />
        <Dialog.Content className="card dialog__content">
          <Dialog.Title asChild>
            <h2 style={{ marginBottom: 'var(--space-4)' }}>{title}</h2>
          </Dialog.Title>
          <Dialog.Description className="visually-hidden">{title}</Dialog.Description>
          <MasterDataForm
            {...rest}
            onCancel={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function labelKeyFor(column: string): string {
  switch (column) {
    case 'name':
      return 'masterdata.name';
    case 'email_address':
      return 'masterdata.emailAddress';
    case 'is_default':
      return 'masterdata.isDefault';
    case 'is_active':
      return 'masterdata.isActive';
    case 'sort_order':
      return 'masterdata.sortOrder';
    case 'color':
      return 'masterdata.color';
    case 'level':
      return 'masterdata.level';
    case 'state_category':
      return 'masterdata.stateCategory';
    case 'pauses_sla':
      return 'masterdata.pausesSla';
    case 'key':
      return 'masterdata.typeKey';
    default:
      return column;
  }
}

function CellValue({ column, entry }: { column: string; entry: MasterDataEntry }) {
  const { t } = useTranslation();
  const value = entry[column];

  if (typeof value === 'boolean') {
    return value ? <Badge tone="info">{t('masterdata.yes')}</Badge> : <span>{t('app.none')}</span>;
  }

  if (column === 'color' && typeof value === 'string') {
    return (
      <span className="row">
        <span
          aria-hidden="true"
          style={{
            width: '1rem',
            height: '1rem',
            borderRadius: 'var(--radius-control)',
            backgroundColor: value,
            border: '1px solid var(--color-border)',
          }}
        />
        <span className="table__muted">{value}</span>
      </span>
    );
  }

  if (column === 'state_category' && typeof value === 'string') {
    return <>{t(`stateCategory.${value}`)}</>;
  }

  if (column === 'key' && typeof value === 'string') {
    return <>{t(`ticketType.${value}`)}</>;
  }

  if (value === null || value === undefined || value === '') {
    return <span className="table__muted">{t('app.none')}</span>;
  }

  return <>{toInputValue(value)}</>;
}
